import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Autocomplete, Box, Grid, Skeleton, TextField } from "@mui/material"
import styles from "./DashboardInput.module.css";

const EXCHANGE_RATE_FALLBACK_URL = "https://api.exchangerate-api.com/v4/latest/INR";
const STATIC_CURRENCY_CODES = [
  "INR", "USD", "EUR", "GBP", "AED", "AUD", "CAD", "CHF", "CNY", "JPY",
  "SGD", "HKD", "NZD", "SAR", "QAR", "KWD", "BHD", "OMR", "ZAR", "SEK",
  "NOK", "DKK", "THB", "MYR", "IDR", "PHP", "KRW", "VND", "PKR", "BDT",
  "LKR", "NPR", "EGP", "NGN", "KES", "GHS", "TRY", "RUB", "MXN", "BRL",
];

const getCurrencyDisplayName = (code) => {
  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "currency" });
    return displayNames.of(code) || code;
  } catch (error) {
    return code;
  }
};

const normalizeCurrencyOnlyOption = (currencyCode) => {
  const code = String(currencyCode || "").trim().toUpperCase();
  if (!code) return null;

  return {
    flag: "",
    flags: "",
    countryName: getCurrencyDisplayName(code),
    currency: code,
  };
};

const SelectCountry = (props) => {
  const { value, setValue, label, width, disableUpd, margin=0, gridMd = 3 } = props;
  const fieldError = props.error;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCountries = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(EXCHANGE_RATE_FALLBACK_URL);
        const rateKeys = Object.keys(response?.data?.rates || {});
        const responseData = rateKeys
          .map(normalizeCurrencyOnlyOption)
          .filter(Boolean);

        if (isMounted) {
          setData(responseData);
          setLoading(false);
        }
        return;
      } catch (fetchError) {
        console.log("Exchange-rate currency source failed, using static fallback:", fetchError);
      }

      try {
        const fallbackOptions = STATIC_CURRENCY_CODES
          .map(normalizeCurrencyOnlyOption)
          .filter(Boolean);

        if (isMounted) {
          setData(fallbackOptions);
          setLoading(false);
        }
      } catch (fallbackError) {
        if (isMounted) {
          setError(fallbackError);
          setLoading(false);
        }
      }
    };

    fetchCountries();

    return () => {
      isMounted = false;
    };
  }, []);

  const dataCountries = useMemo(
    () =>
      (Array.isArray(data) ? data : [])
        .map((item) => item?.currency && item?.countryName ? item : null)
        .filter(Boolean)
        .filter(
          (item, index, arr) =>
            arr.findIndex((entry) => entry.currency === item.currency) === index,
        )
        .sort((a, b) => String(a.currency).localeCompare(String(b.currency))),
    [data],
  );

  if (loading) {
    return (
      <Grid item xs={12} md={3}>
        <Skeleton variant="rounded" height={60} />
      </Grid>
    )
  }
  if (error) {
    return (
      <Grid item xs={12} md={3}>
        <div className={styles.d_input_container}>
          <label>{label}</label>
          <div className={styles.inputsborder}>
            Failed to load currencies
          </div>
        </div>
      </Grid>)
  }

  return (
    // add margin from parent component
    <Grid
      item
      xs={12}
      md={gridMd}
      style={{
        width:
          width === true
            ? "45%"
            : typeof width === "string"
            ? width
            : "100%",
        margin: props.margin ? props.margin : "0",
      }}
    >
      <div className={styles.d_input_container}>
        <label>{label}</label>
        <div className={styles.inputsborder}>
          <Autocomplete
            disabled={disableUpd}
            value={value}
            disableClearable={props.disableClearable ?? true}
            autoHighlight={true}
            autoComplete={true}
            sx={{
              height: "4.2rem",
              fontSize: "1.4rem",
              transition: "0",
              " MuiOutlinedInput-root": {
                paddingRight: '20px',
              },
              ".Mui-expanded": {
                transition: "none",
              },
              ".MuiOutlinedInput-notchedOutline": {
                border: "0",
              },
              ".MuiAutocomplete-input": {
                fontSize: "1.4rem",
                fontFamily: "GilroyMedium",
                height: "2.6rem",
              },
            }}
            componentsProps={{
              popper: {
                sx: {
                  fontFamily: "gilroyMedium",
                },
                modifiers: [
                  {
                    name: "flip",
                    enabled: false,
                  },
                  {
                    name: "preventOverflow",
                    enabled: true,
                  },
                ],
              },
            }}
            size="small"
            className={`${styles.select_wrapper_multiSelect}  ${(error || fieldError) && styles.error
              }`}
            onChange={(event, newValue) => {
              setValue(newValue || { currency: "", countryName: "" });
              props.onChange?.(newValue);

            }}
            getOptionLabel={(option) => (`${option.countryName ? option.countryName : ''} - ${option.currency ? option.currency : ''}`)}
            renderOption={(props, option) => <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                {option.flags ? <img
                loading="lazy"
                width="20"
                srcSet={option.flags}
                src={option.flags}
                alt="" 
                /> : null}
                {option.countryName}
                {option.currency ? `- ${option.currency}` : ''}
          </Box>}
            options={dataCountries}
            renderInput={(params) => <TextField {...params} />}
          />
          {fieldError && <p className={styles.error_message}>{props.errorMsg}</p>}
        </div>
      </div>
    </Grid>
  )
}

export default SelectCountry
